import React, { useEffect, useState } from 'react';
import { Building2, Shield, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '@vaiu/ui/button';
import { Checkbox } from '@vaiu/ui/checkbox';
import { Label } from '@vaiu/ui/label';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { PaymentMethod, platformApi } from '@/services/platformApi';
import { useStripe } from '@stripe/react-stripe-js';
import { useAuth } from '@/context/AuthContext';
import ConfirmIdentity, {
  getDisplayNameAndStatus,
  type ProfileNameSource,
} from '@/components/profile/ConfirmIdentity';

interface StripeBankFormProps {
  onSubmit: (method: PaymentMethod) => Promise<void>;
  loading: boolean;
  onCancel: () => void;
}

// Stripe.js collectFinancialConnectionsAccounts - may not be in @stripe/stripe-js types yet
declare global {
  interface Stripe {
    collectFinancialConnectionsAccounts?(params: { clientSecret: string }): Promise<{
      accounts: Array<{ id: string }>;
    }>;
  }
}

const StripeBankForm: React.FC<StripeBankFormProps> = ({
  onSubmit,
  loading,
  onCancel,
}) => {
  const stripe = useStripe();
  const { user } = useAuth();
  const [profileForIdentity, setProfileForIdentity] = useState<ProfileNameSource | null>(user);
  const { displayName, hasFullName } = getDisplayNameAndStatus(profileForIdentity);

  useEffect(() => {
    let cancelled = false;

    const loadProfileForIdentity = async () => {
      try {
        const freshProfile = await platformApi.getProfile();
        if (!cancelled) {
          setProfileForIdentity(freshProfile);
        }
      } catch (err) {
        console.warn('[StripeBankForm] Failed to refresh profile for identity check:', err);
      }
    };

    void loadProfileForIdentity();

    return () => {
      cancelled = true;
    };
  }, []);

  const [identityConfirmed, setIdentityConfirmed] = useState(false);
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);

  if (!identityConfirmed) {
    return (
      <ConfirmIdentity
        displayName={displayName}
        hasFullName={hasFullName}
        onConfirm={() => setIdentityConfirmed(true)}
        onCancel={onCancel}
        loading={loading}
      />
    );
  }

  const handleConnectClick = async () => {
    if (!stripe) {
      setError('Payment system not ready. Please try again.');
      return;
    }

    setConnecting(true);
    setError(null);

    try {
      // 1. Create Financial Connections session
      const { clientSecret } = await platformApi.createStripeBankSession();

      // 2. Launch Stripe Financial Connections UI
      const collectFn = (stripe as any).collectFinancialConnectionsAccounts ?? stripe.collectFinancialConnectionsAccounts;
      if (!collectFn) {
        throw new Error('Stripe Financial Connections is not available. Please ensure you are using a supported Stripe.js version.');
      }
      const result = await collectFn.call(stripe, { clientSecret });
      if (!result) {
        throw new Error('Bank connection was cancelled or did not complete.');
      }

      // Stripe returns { financialConnectionsSession: { accounts: [{ id: "fca_xxx", ... }] } }
      const session = result?.financialConnectionsSession ?? result?.session;
      const accountsList = session?.accounts ?? result?.accounts;
      const accounts = Array.isArray(accountsList)
        ? accountsList
        : Array.isArray(accountsList?.data)
          ? accountsList.data
          : Array.isArray(result?.data)
            ? result.data
            : [];
      const firstAccount = accounts[0];
      if (!firstAccount || !(typeof firstAccount === 'object' && firstAccount?.id)) {
        throw new Error('No bank account was selected. Please try again.');
      }
      const financialConnectionsAccountId = firstAccount.id;

      // 3. Attach bank account (creates SetupIntent) - pass confirmed display name for billing_details
      const { clientSecret: setupIntentClientSecret } = await platformApi.attachStripeBankAccount(
        financialConnectionsAccountId,
        displayName
      );

      // 4. Confirm SetupIntent
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        clientSecret: setupIntentClientSecret,
        confirmParams: {
          return_url: window.location.href,
        },
      });

      if (confirmError) {
        throw new Error(confirmError.message ?? 'Failed to confirm bank account');
      }
      if (!setupIntent || setupIntent.status !== 'succeeded') {
        throw new Error('Bank account setup was not completed. Please try again.');
      }
      const setupIntentId = typeof setupIntent.id === 'string' ? setupIntent.id : setupIntent.id;

      // 5. Save bank account to backend
      const savedPaymentMethod = await platformApi.saveStripeBankAccount(setupIntentId);

      // Update the isDefault preference if needed
      if (setAsDefault && savedPaymentMethod?.id) {
        await platformApi.setDefaultPaymentMethod(savedPaymentMethod.id);
        savedPaymentMethod.isDefault = true;
      } else {
        savedPaymentMethod.isDefault = setAsDefault;
      }

      await onSubmit(savedPaymentMethod);
    } catch (err: any) {
      console.error('Stripe bank connect failed:', err);
      setError(err.message ?? 'Failed to connect bank account');
    } finally {
      setConnecting(false);
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        <div className="bg-muted/50 p-4 rounded-lg">
          <h4 className="font-medium mb-2">How it works:</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Securely connect your bank account through Stripe</li>
            <li>• We&apos;ll verify your account for ACH transfers</li>
            <li>• Your login credentials are never stored</li>
            <li>• Bank-level security and encryption</li>
          </ul>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="setAsDefault"
            checked={setAsDefault}
            onCheckedChange={(checked) => setSetAsDefault(checked as boolean)}
          />
          <Label htmlFor="setAsDefault" className="text-sm">
            Set as default payment method
          </Label>
        </div>

        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Shield className="w-4 h-4" />
          <span>Bank-grade security powered by Stripe</span>
        </div>

        <Button
          onClick={handleConnectClick}
          disabled={loading || connecting || !stripe}
          className="w-full"
        >
          {connecting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Connecting...
            </>
          ) : (
            <>
              <Building2 className="w-4 h-4 mr-2" />
              Connect Bank Account
              <ExternalLink className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>

        <div className="text-xs text-muted-foreground text-center">
          By connecting your bank account, you agree to Stripe&apos;s{' '}
          <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">
            Privacy Policy
          </a>{' '}
          and{' '}
          <a href="https://stripe.com/legal" target="_blank" rel="noopener noreferrer" className="underline">
            Terms of Service
          </a>
        </div>
      </div>

      <div className="flex space-x-2 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading || connecting}
          className="flex-1"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default StripeBankForm;
